import { MatDialog } from '@angular/material/dialog';
import { Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ROUTER_NAVIGATED } from '@ngrx/router-store';

import { of } from 'rxjs';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';

import { ToastrService } from 'ngx-toastr';

import * as moment from 'moment';

import * as releaseNotesActions from './release-note.actions';
import { DashboardCamelCasePipe } from 'src/app/core/pipes/dashboard-camel-case/dashboard-camel-case.pipe';
import { ReleasesService } from '../../services/releases/releases.service';
import { ReleaseNote } from 'src/app/models/release-note';
import { CustomRouterNavigatedAction } from 'src/app/core/custom-route-serializer';
import { DASHBOARD_ROUTES } from '../../dashboard.routes';
import { SETTINGS_ROUTES } from '../../settings/settings.routes';

@Injectable()
export class ReleaseNoteEffects {
  releaseNotesRoute$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CustomRouterNavigatedAction>(ROUTER_NAVIGATED),
      filter((route: CustomRouterNavigatedAction) => {
        return (
          route.payload.routerState.path === DASHBOARD_ROUTES.ROOT ||
          route.payload.routerState.path === DASHBOARD_ROUTES.STUDIES ||
          route.payload.routerState.path === SETTINGS_ROUTES.RELEASE_NOTES
        );
      }),
      map(() => releaseNotesActions.getReleaseNotes())
    )
  );

  getReleaseNotes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(releaseNotesActions.getReleaseNotes),
      switchMap(() => {
        return this.releaseNotesService.getReleases().pipe(
          map((releaseNotes) => {
            const transformedReleaseNotes = releaseNotes.map((releaseNote) => {
              const transformedReleaseNote: ReleaseNote = this.dashboardCamelCasePipe.keysToCamelCase(releaseNote);
              transformedReleaseNote.createdAt = moment(transformedReleaseNote.createdAt).format('dddd, MMMM D, YYYY');
              return transformedReleaseNote;
            });
            return releaseNotesActions.getReleaseNotesSuccess({
              releaseNotes: transformedReleaseNotes,
            });
          }),
          catchError(() => of(releaseNotesActions.getReleaseNotesError()))
        );
      })
    )
  );

  getReleaseNotesError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(releaseNotesActions.getReleaseNotesError),
        tap(() => this.toastrService.error('Error in fetching Release Notes'))
      ),
    { dispatch: false }
  );

  saveReleaseNote$ = createEffect(() =>
    this.actions$.pipe(
      ofType(releaseNotesActions.saveReleaseNote),
      switchMap(({ releaseNote }) => {
        return this.releaseNotesService.saveReleaseNote(releaseNote).pipe(
          map(() => releaseNotesActions.saveReleaseNoteSuccess()),
          catchError(() => of(releaseNotesActions.saveReleaseNoteError()))
        );
      })
    )
  );

  saveReleaseNotesSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(releaseNotesActions.saveReleaseNoteSuccess),
      switchMap(() => of(releaseNotesActions.getReleaseNotes())),
      tap(() => {
        this.toastrService.success('Release note successfully saved.');
        this.dialog.closeAll();
      })
    )
  );

  saveReleaseNotesError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(releaseNotesActions.saveReleaseNoteError),
        tap(() => this.toastrService.error('Unable to create release note.'))
      ),
    { dispatch: false }
  );

  deleteReleaseNote$ = createEffect(() =>
    this.actions$.pipe(
      ofType(releaseNotesActions.deleteReleaseNote),
      switchMap(({ releaseNoteId }) => {
        return this.releaseNotesService.deleteReleaseNote(releaseNoteId).pipe(
          map(() =>
            releaseNotesActions.deleteReleaseNoteSuccess({
              deletedReleaseNoteId: releaseNoteId,
            })
          ),
          catchError(() => of(releaseNotesActions.deleteReleaseNoteError()))
        );
      })
    )
  );

  deleteReleaseNotesSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(releaseNotesActions.deleteReleaseNoteSuccess),
        tap(() => this.toastrService.success('Release note successfully deleted.'))
      ),
    { dispatch: false }
  );

  deleteReleaseNotesError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(releaseNotesActions.deleteReleaseNoteError),
        tap(() => this.toastrService.error('Unable to delete release note.'))
      ),
    { dispatch: false }
  );

  ediReleaseNote$ = createEffect(() =>
    this.actions$.pipe(
      ofType(releaseNotesActions.editReleaseNote),
      switchMap(({ releaseNote }) => {
        return this.releaseNotesService.editReleaseNote(releaseNote.id, releaseNote).pipe(
          map((editedReleaseNote) => releaseNotesActions.editReleaseNoteSuccess({ editedReleaseNote })),
          catchError(() => of(releaseNotesActions.editReleaseNoteError()))
        );
      })
    )
  );

  editReleaseNotesSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(releaseNotesActions.editReleaseNoteSuccess),
        tap(() => {
          this.toastrService.success('Release note successfully edited.');
          this.dialog.closeAll();
        })
      ),
    { dispatch: false }
  );

  editReleaseNotesError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(releaseNotesActions.editReleaseNoteError),
        tap(() => this.toastrService.error('Unable to edit release note.'))
      ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private releaseNotesService: ReleasesService,
    private toastrService: ToastrService,
    private dashboardCamelCasePipe: DashboardCamelCasePipe,
    private dialog: MatDialog
  ) {}
}
